import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, Rocket, Loader2, CheckCircle2 } from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LightBackground from '@/components/LightBackground';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

const INDUSTRIES = [
  'E-commerce / DTC', 'Education / Online Courses', 'SaaS / Software', 'Content / Media',
  'Health & Wellness', 'Finance / Crypto', 'Real Estate', 'Coaching / Consulting',
  'Local Services', 'AI / Tech Tools', 'Entertainment / Gaming', 'Other',
];

const BUSINESS_TYPES = [
  'Faceless Content Brand', 'Online Store / E-commerce', 'Digital Product / Course',
  'SaaS / App', 'Agency / Service Business', 'Membership / Community',
  'Affiliate / Niche Site', 'Personal Brand', 'Other',
];

const BUSINESS_MODELS = [
  'Subscription / Recurring', 'One-time digital product', 'High-ticket service',
  'Physical product / dropshipping', 'Ad / sponsorship revenue', 'Affiliate commissions',
  'Mixed / hybrid', 'Not sure yet',
];

const TIMELINES = [
  'ASAP — within 2 weeks', 'Standard — 14 to 21 days', 'Flexible — 30 days',
  'Phased rollout — 30 to 60 days',
];

const BUDGETS = [
  'Up to $2,000', '$2,000 – $5,000', '$5,000 – $10,000', '$10,000 – $25,000',
  '$25,000 – $50,000', '$50,000+',
];

const PLATFORMS = [
  'Website', 'Mobile App (iOS/Android)', 'YouTube', 'TikTok', 'Instagram',
  'X / Twitter', 'Email / Newsletter', 'Telegram / Discord', 'Stripe Checkout', 'Shopify',
];

const MONETIZATION = [
  'Subscriptions', 'One-time payments', 'High-ticket offers',
  'Ads / Sponsorships', 'Affiliate revenue', 'Mixed', 'Not sure',
];

const FACELESS = [
  'Fully faceless (no founder visible)',
  'Semi-faceless (voice only / brand persona)',
  'Founder-led (face visible)',
  'No preference',
];

const briefSchema = z.object({
  full_name: z.string().trim().min(2, 'Name is required').max(100),
  email: z.string().trim().email('Valid email required').max(255),
  project_name: z.string().trim().min(2, 'Project name is required').max(120),
  industry: z.string().min(1, 'Select an industry'),
  business_type: z.string().min(1, 'Select a business type'),
  business_model: z.string().optional(),
  target_audience: z.string().trim().max(500).optional(),
  description: z.string().trim().min(40, 'Please describe your project in at least 40 characters').max(3000),
  primary_goal: z.string().trim().max(500).optional(),
  investment_expected: z.string().trim().max(120).optional(),
  budget_range: z.string().optional(),
  already_has: z.string().trim().max(1000).optional(),
  timeline: z.string().min(1, 'Select a timeline'),
  monetization_preference: z.string().optional(),
  faceless_preference: z.string().optional(),
  competitors: z.string().trim().max(500).optional(),
  inspiration_links: z.string().trim().max(500).optional(),
  languages: z.string().trim().max(120).optional(),
  countries_targeted: z.string().trim().max(200).optional(),
  additional_notes: z.string().trim().max(1500).optional(),
});

const LaunchKitBrief = () => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    project_name: '',
    industry: '',
    business_type: '',
    business_model: '',
    target_audience: '',
    description: '',
    primary_goal: '',
    investment_expected: '',
    budget_range: '',
    already_has: '',
    timeline: '',
    monetization_preference: '',
    faceless_preference: '',
    competitors: '',
    inspiration_links: '',
    languages: '',
    countries_targeted: '',
    additional_notes: '',
  });
  const [has, setHas] = useState({
    brand: false, domain: false, logo: false, audience: false, content: false,
  });
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (user?.email) setForm((f) => ({ ...f, email: user.email || '' }));
  }, [user]);

  const update = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const togglePlatform = (p: string) =>
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      toast.error('Please confirm the terms before submitting.');
      return;
    }

    const result = briefSchema.safeParse(form);
    if (!result.success) {
      toast.error(result.error.issues[0].message);
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('launch_kit_briefs')
        .insert([{
          user_id: user?.id ?? null,
          ...result.data,
          has_brand: has.brand,
          has_domain: has.domain,
          has_logo: has.logo,
          has_audience: has.audience,
          has_content: has.content,
          preferred_platforms: platforms,
          agreed_terms: true,
        } as never]);

      if (error) throw error;

      // Fire-and-forget Telegram notification (admin only) — FULL brief
      const d = result.data;
      const esc = (s: string | undefined | null) =>
        (s ?? '—').toString().replace(/</g, '&lt;').replace(/>/g, '&gt;').slice(0, 600) || '—';
      const hasList = [
        has.brand && 'Brand',
        has.domain && 'Domain',
        has.logo && 'Logo',
        has.audience && 'Audience',
        has.content && 'Content',
      ].filter(Boolean).join(', ') || 'None';

      const telegramText =
        `📩 <b>New Project Request — AI Business Launch Kit</b>\n` +
        `🕒 ${new Date().toLocaleString('en-US')}\n\n` +
        `<b>👤 Contact</b>\n` +
        `Name: ${esc(d.full_name)}\n` +
        `Email: ${esc(d.email)}\n\n` +
        `<b>🏷 Project</b>\n` +
        `Name: ${esc(d.project_name)}\n` +
        `Industry: ${esc(d.industry)}\n` +
        `Business Type: ${esc(d.business_type)}\n` +
        `Business Model: ${esc(d.business_model)}\n` +
        `Timeline: ${esc(d.timeline)}\n` +
        `Budget: ${esc(d.budget_range)}\n` +
        `Investment Expected: ${esc(d.investment_expected)}\n\n` +
        `<b>🎯 Strategy</b>\n` +
        `Target Audience: ${esc(d.target_audience)}\n` +
        `Primary Goal: ${esc(d.primary_goal)}\n` +
        `Monetization: ${esc(d.monetization_preference)}\n` +
        `Faceless Style: ${esc(d.faceless_preference)}\n` +
        `Languages: ${esc(d.languages)}\n` +
        `Countries: ${esc(d.countries_targeted)}\n\n` +
        `<b>🛠 Assets & Platforms</b>\n` +
        `Already Has: ${hasList}\n` +
        `Platforms: ${platforms.join(', ') || '—'}\n` +
        `Already Built: ${esc(d.already_has)}\n\n` +
        `<b>🔍 References</b>\n` +
        `Competitors: ${esc(d.competitors)}\n` +
        `Inspiration: ${esc(d.inspiration_links)}\n\n` +
        `<b>📝 Description</b>\n${esc(d.description)}\n\n` +
        `<b>💬 Additional Notes</b>\n${esc(d.additional_notes)}`;

      supabase.functions.invoke('telegram-notify', {
        body: { text: telegramText.slice(0, 3900) },
      }).catch(() => {});

      toast.success('Brief submitted! Our team will review it within a few days.');

      if (!user || !session) {
        navigate(`/login?redirect=/launch-kit/status`);
        return;
      }
      navigate(`/launch-kit/status`);
    } catch (err) {
      console.error(err);
      toast.error('Could not submit. Please try again or email hello@auradynamics.management');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Project Brief — AI Business Launch Kit | Faceless Aura Club</title>
        <meta name="description" content="Tell us about your project so our team can build your AI business blueprint, ready to launch in 14 days." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />

        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/launch-kit" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Launch Kit</span>
            </Link>
            <span className="text-sm text-slate-500">Step 1 of 2 · Project Brief</span>
          </div>
        </header>

        <main className="relative z-10 max-w-3xl mx-auto px-6 py-10 md:py-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-5">
              <Rocket className="w-7 h-7 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Project Brief</h1>
            <p className="text-slate-600 max-w-xl mx-auto">
              The more detail you give us, the sharper your blueprint. Fill this in once — we use it to build, invoice, and deliver your project.
            </p>
          </motion.div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-10 space-y-10"
          >
            {/* Contact */}
            <section className="space-y-5">
              <h2 className="text-lg font-semibold text-slate-900">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full name *</Label>
                  <Input id="full_name" value={form.full_name} onChange={(e) => update('full_name', e.target.value)} maxLength={100} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} maxLength={255} required />
                </div>
              </div>
            </section>

            {/* Project */}
            <section className="space-y-5">
              <h2 className="text-lg font-semibold text-slate-900">The project</h2>
              <div className="space-y-2">
                <Label htmlFor="project_name">Project / business name *</Label>
                <Input id="project_name" value={form.project_name} onChange={(e) => update('project_name', e.target.value)} maxLength={120} required />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry *</Label>
                  <Select value={form.industry} onValueChange={(v) => update('industry', v)}>
                    <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Business type *</Label>
                  <Select value={form.business_type} onValueChange={(v) => update('business_type', v)}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business model</Label>
                <Select value={form.business_model} onValueChange={(v) => update('business_model', v)}>
                  <SelectTrigger><SelectValue placeholder="How will it make money?" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_MODELS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Full written description *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  maxLength={3000}
                  rows={6}
                  placeholder="What is the project? What problem does it solve? What does success look like in 6 months?"
                  required
                />
                <p className="text-xs text-slate-500">{form.description.length} / 3000</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target_audience">Target audience</Label>
                <Textarea
                  id="target_audience"
                  value={form.target_audience}
                  onChange={(e) => update('target_audience', e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="Who is this for? Age, location, behaviours, pain points."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="primary_goal">Primary goal of this build</Label>
                <Textarea
                  id="primary_goal"
                  value={form.primary_goal}
                  onChange={(e) => update('primary_goal', e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="First $10k month? 1,000 subscribers? Launch a product?"
                />
              </div>
            </section>

            {/* Investment & timeline */}
            <section className="space-y-5">
              <h2 className="text-lg font-semibold text-slate-900">Investment &amp; timeline</h2>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="investment_expected">Investment expected to build it</Label>
                  <Input
                    id="investment_expected"
                    value={form.investment_expected}
                    onChange={(e) => update('investment_expected', e.target.value)}
                    maxLength={120}
                    placeholder="e.g. $3,000 build + $500/mo ops"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Overall budget range</Label>
                  <Select value={form.budget_range} onValueChange={(v) => update('budget_range', v)}>
                    <SelectTrigger><SelectValue placeholder="Select budget" /></SelectTrigger>
                    <SelectContent>
                      {BUDGETS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>How fast do you need the project on air? *</Label>
                <RadioGroup value={form.timeline} onValueChange={(v) => update('timeline', v)} className="grid sm:grid-cols-2 gap-2">
                  {TIMELINES.map((t) => (
                    <Label key={t} className="flex items-center gap-2 border border-slate-200 rounded-lg p-3 cursor-pointer hover:border-blue-400 transition">
                      <RadioGroupItem value={t} />
                      <span className="text-sm">{t}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            </section>

            {/* What you already have */}
            <section className="space-y-5">
              <h2 className="text-lg font-semibold text-slate-900">What you already have</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ['brand', 'Brand / name decided'],
                  ['domain', 'Domain registered'],
                  ['logo', 'Logo / visual identity'],
                  ['audience', 'Existing audience'],
                  ['content', 'Content / assets ready'],
                ].map(([k, label]) => (
                  <Label key={k} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 cursor-pointer hover:border-blue-400">
                    <Checkbox
                      checked={has[k as keyof typeof has]}
                      onCheckedChange={(v) => setHas((s) => ({ ...s, [k]: !!v }))}
                    />
                    <span className="text-sm">{label}</span>
                  </Label>
                ))}
              </div>
              <div className="space-y-2">
                <Label htmlFor="already_has">Anything else you already have?</Label>
                <Textarea
                  id="already_has"
                  value={form.already_has}
                  onChange={(e) => update('already_has', e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder="Existing accounts, customers, partnerships, code, content libraries…"
                />
              </div>
            </section>

            {/* Platforms */}
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-900">Preferred platforms</h2>
              <p className="text-sm text-slate-500">Pick everything that should be set up.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PLATFORMS.map((p) => (
                  <Label key={p} className="flex items-center gap-2 border border-slate-200 rounded-lg p-3 cursor-pointer hover:border-blue-400">
                    <Checkbox checked={platforms.includes(p)} onCheckedChange={() => togglePlatform(p)} />
                    <span className="text-sm">{p}</span>
                  </Label>
                ))}
              </div>
            </section>

            {/* Preferences */}
            <section className="space-y-5">
              <h2 className="text-lg font-semibold text-slate-900">Preferences</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monetization preference</Label>
                  <Select value={form.monetization_preference} onValueChange={(v) => update('monetization_preference', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {MONETIZATION.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Faceless preference</Label>
                  <Select value={form.faceless_preference} onValueChange={(v) => update('faceless_preference', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {FACELESS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="languages">Languages</Label>
                  <Input id="languages" value={form.languages} onChange={(e) => update('languages', e.target.value)} maxLength={120} placeholder="e.g. English, Portuguese" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="countries_targeted">Countries / markets targeted</Label>
                  <Input id="countries_targeted" value={form.countries_targeted} onChange={(e) => update('countries_targeted', e.target.value)} maxLength={200} placeholder="e.g. USA, UAE, EU" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="competitors">Competitors / benchmarks</Label>
                <Textarea id="competitors" value={form.competitors} onChange={(e) => update('competitors', e.target.value)} maxLength={500} rows={2} placeholder="Brands or creators you want to compete with." />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inspiration_links">Inspiration links</Label>
                <Textarea id="inspiration_links" value={form.inspiration_links} onChange={(e) => update('inspiration_links', e.target.value)} maxLength={500} rows={2} placeholder="Websites, social profiles, products you love." />
              </div>

              <div className="space-y-2">
                <Label htmlFor="additional_notes">Anything else we should know?</Label>
                <Textarea id="additional_notes" value={form.additional_notes} onChange={(e) => update('additional_notes', e.target.value)} maxLength={1500} rows={4} placeholder="Constraints, must-haves, things to avoid…" />
              </div>
            </section>

            {/* Terms */}
            <section className="space-y-4">
              <Label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-1" />
                <span className="text-sm text-slate-700">
                  I confirm the information provided is accurate and I have read the{' '}
                  <Link to="/terms" className="text-blue-600 underline">Terms</Link>,{' '}
                  <Link to="/refunds" className="text-blue-600 underline">Refund Policy</Link>, and{' '}
                  <Link to="/payments-policy" className="text-blue-600 underline">Payments Policy</Link>.
                  Final scope and price are confirmed in the invoice after this brief is reviewed.
                </span>
              </Label>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-slate-700 flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <p>
                  Pricing starts at <strong>$1,799</strong>. Complex scopes may require a custom quote — if so, our team will reach out before
                  charging anything beyond the base fee.
                </p>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold text-base h-14 rounded-xl shadow-lg shadow-accent/20"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Rocket className="w-5 h-5 mr-2" />}
                Submit Brief &amp; Continue to Payment
              </Button>
            </section>
          </form>
        </main>
      </div>
    </>
  );
};

export default LaunchKitBrief;
